// src/features/dashboard/components/SystemHealthSection.tsx

import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import {
  Warning,
  Computer,
  Lock,
  Assignment,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { DashboardSummaryResponse } from '../types';

interface SystemHealthSectionProps {
  data: DashboardSummaryResponse;
}

interface StatusCardProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  value?: number;
  status: 'success' | 'warning' | 'error';
  showValueOnly?: boolean;
}

const StatusCard: React.FC<StatusCardProps> = ({
  title,
  subtitle,
  icon,
  value,
  status,
  showValueOnly = false
}) => {
  const translate = useTranslate();

  const getStatusIcon = () => {
    if (showValueOnly && value !== undefined) {
      return (
        <Typography variant="h6" sx={{ color: getStatusColor(), fontWeight: 'bold' }}>
          {value}
        </Typography>
      );
    }
    
    switch (status) {
      case 'success':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: 24 }} />;
      case 'warning':
        return <Warning sx={{ color: '#ff9800', fontSize: 24 }} />;
      case 'error':
        return <Error sx={{ color: '#f44336', fontSize: 24 }} />;
      default:
        return <CheckCircle sx={{ color: '#4caf50', fontSize: 24 }} />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#4caf50';
      case 'warning':
        return '#ff9800';
      case 'error':
        return '#f44336';
      default:
        return '#4caf50';
    }
  };

  return (
    <Card sx={{
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }
    }}>
      <CardContent sx={{ 
        p: 2,
        '&:last-child': { pb: 2 }
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 60
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            flex: 1
          }}>
            <Box sx={{ color: '#666' }}>
              {React.cloneElement(icon as React.ReactElement, { 
                sx: { fontSize: 24 } 
              })}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ 
                fontSize: '0.8rem', 
                fontWeight: 600,
                color: '#333',
                lineHeight: 1.2,
                mb: 0.5
              }}>
                {translate(title)}
              </Typography>
              <Typography variant="caption" sx={{ 
                fontSize: '0.7rem',
                color: '#999',
                display: 'block',
                lineHeight: 1.2
              }}>
                {translate(subtitle)}
              </Typography>
            </Box>
          </Box>
          <Box>
            {getStatusIcon()}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const SystemHealthSection: React.FC<SystemHealthSectionProps> = ({ data }) => {
  // Calculate values
  const hmiOnline = data.onlineHMIs || 0;
  const hmiTotal = data.totalHMIs || 0;
  const blockedLockers = data.blockedLockers || 0;
  const expiredAssignments = data.expiredActiveAssignments || 0;

  return (
    <Grid container spacing={2}>
      {/* HMI Devices */}
      <Grid item xs={12} sm={6} md={4}>
        <StatusCard
          title="resources.dashboard.hmi_devices"
          subtitle="resources.dashboard.devices_online"
          icon={<Computer />}
          value={hmiOnline}
          status={hmiOnline === hmiTotal && hmiTotal > 0 ? 'success' : hmiTotal === 0 ? 'warning' : 'error'}
          showValueOnly={true}
        />
      </Grid>

      {/* Blocked Lockers */}
      <Grid item xs={12} sm={6} md={4}>
        <StatusCard
          title="resources.dashboard.blocked_lockers"
          subtitle="resources.dashboard.lockers_awaiting_clearance"
          icon={<Lock />}
          status={blockedLockers === 0 ? 'success' : 'warning'}
        />
      </Grid>

      {/* Expired Assignments */}
      <Grid item xs={12} sm={6} md={4}>
        <StatusCard
          title="resources.dashboard.expired_assignments"
          subtitle="resources.dashboard.assignments_past_due"
          icon={<Assignment />}
          status={expiredAssignments === 0 ? 'success' : 'warning'}
        />
      </Grid>
    </Grid>
  );
};

export default SystemHealthSection;